import { forwardRef, useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";

/**
 * Beyond Sure — Checkbox primitive.
 *
 * Native <input type=checkbox> with the default chrome stripped, swapped for
 * a custom 18px square that follows the brand hierarchy:
 *   unchecked → Paper surface, Ink-16% border
 *   checked   → BRAND-GREEN fill, Paper check mark, no border (NOT blue)
 *   focus     → Brand-green ring (matches Button focus ring)
 *
 * The visible square is purely decorative — the real input lives behind it
 * (peer-checked drives the paint state) so the native checked/focus/
 * keyboard semantics are preserved verbatim.
 */
interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "type"> {
  label: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ label, className = "", ...rest }, ref) {
    const id = useId();
    return (
      <label
        htmlFor={id}
        className={[
          "group inline-flex items-start gap-3 cursor-pointer select-none",
          "text-[13.5px] leading-[1.5] text-[var(--color-ink-80)]",
          className,
        ].join(" ")}
      >
        <input
          ref={ref}
          id={id}
          type="checkbox"
          className="peer sr-only"
          {...rest}
        />

        <span
          aria-hidden="true"
          className={[
            "mt-[2px] inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center",
            "rounded-[6px] border bg-[var(--color-paper)] border-[var(--color-ink-16)]",
            "transition-[background-color,border-color,box-shadow]",
            "duration-[var(--duration-fast)] ease-[var(--ease-standard)]",
            // Checked: brand-green fill (the identity color is owning the
            // 'opted-in' affordance, while the primary action button stays
            // black — a deliberate split between identity and action).
            "peer-checked:bg-[var(--color-brand)] peer-checked:border-[var(--color-brand)]",
            // Focus ring (mirrors Button — same brand green)
            "peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--color-brand)]",
            "peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[var(--color-paper)]",
          ].join(" ")}
        >
          {/* Check mark — paper-coloured so it reads on the green fill */}
          <svg
            viewBox="0 0 16 16"
            width="12"
            height="12"
            className="opacity-0 peer-checked:opacity-100 transition-opacity duration-[var(--duration-fast)]"
            style={{ color: "var(--color-paper)" }}
          >
            <path
              d="M3 8.5l3 3 7-7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>

        <span>{label}</span>
      </label>
    );
  },
);

export default Checkbox;
